import { PlumaSnackbarApi } from '@customerio/pluma-components/react';

export const snackbarApi = new PlumaSnackbarApi();

type SnackbarCreateArgs = Parameters<typeof snackbarApi.create>;

// sessionStorage key shared with the Ember app's snackbars service
// (`app/services/snackbars.ts`). Ember-side flows that show a one-time message
// and then hand off to a Hydra-owned route enqueue the message via `createPersisted`;
// the Ember snackbar container unmounts on handoff, so Hydra replays it here.
const PENDING_KEY = 'cio-pending-snackbars';

/**
 * Like `snackbarApi.create`, but also queues the snackbar so it re-fires after a full page
 * reload.
 *
 * For success paths that reload as part of the happy path — the Builder downgrade flips the
 * billing vendor, clears the Ordway customer link and pins a new plan, which is reconciled by
 * reloading rather than by patching every cached model. Without this the confirmation is
 * destroyed by the reload it triggers, so the user is left on a changed page with no
 * acknowledgement that anything happened.
 *
 * Persisting is best-effort: `sessionStorage` can be unavailable (private mode, quota) and the
 * immediate snackbar has already fired by then, so a failure is swallowed.
 *
 * Pass args that auto-close. An `autoCloseTimeout: 0` would survive the reload as a toast that
 * never dismisses itself.
 */
export function createPersistedSnackbar(...args: SnackbarCreateArgs) {
  const result = snackbarApi.create(...args);
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const pending = raw != null ? (JSON.parse(raw) as SnackbarCreateArgs[]) : [];
    pending.push(args);
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Unavailable or malformed — the immediate snackbar already fired.
  }
  return result;
}

/**
 * Re-fires any snackbars the Ember app enqueued via `snackbars.createPersisted`
 * before handing off to a Hydra route, then clears them. Mirrors Ember's
 * `replayPending`, reading the same sessionStorage key and create-args shape.
 * Safe to call repeatedly — the key is cleared before re-firing, so a second
 * call is a no-op.
 */
export function replayPendingSnackbars() {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(PENDING_KEY);
    if (raw == null) {
      return;
    }
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  try {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const pending = JSON.parse(raw) as SnackbarCreateArgs[];
    for (const args of pending) {
      snackbarApi.create(...args);
    }
  } catch {
    // Malformed payload — already cleared above.
  }
}
